<template>
  <v-dialog v-model="isVisible" width="unset" persistent>
    <v-card
      class="d-flex flex-column"
      style="
        max-height: 75vh;
        height: 75vh;
        overflow: hidden;
        max-width: 60vh;
        width: 60vh;
      "
    >
      <v-card-title
        ><div>
          <span class="text--secondary">Select layers to add from</span
          >{{ fileName ? ` ${fileName}` : "" }}
        </div>
        <v-spacer></v-spacer>
        <v-btn icon title="Cancel" @click.prevent="cancel"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </v-card-title>
      <v-card-text class="flex-grow-1" style="overflow: auto">
        <v-form ref="form" @submit.prevent="submit" lazy-validation>
          <!--
              '[{"type":"FeatureCollection","features":[],"fileName":"HQ"},
                {"type":"FeatureCollection","features":[],"fileName":"ParkingLot"}]' -->
          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="indexedLayers"
            item-key="id"
            :items-per-page="-1"
            show-select
            dense
          >
            <template v-slot:item.fileName="{ item }">
              {{ item.fileName }}
            </template>
            <template v-slot:item.features="{ item }">
              {{
                item.features && Array.isArray(item.features)
                  ? Number(item.features.length).toLocaleString()
                  : "N/A"
              }}
            </template>
          </v-data-table>
        </v-form>
      </v-card-text>
      <v-card-actions class="pa-6 pt-2 flex-column">
        <!--
        <div class="mb-2" style="width: 100%">
          Character length:
          {{ Number(selectedCharacterLength).toLocaleString() }}
        </div>
        <div class="mb-2" style="width: 100%">
          Vertext count: {{ Number(vertexCount).toLocaleString() }}
        </div>
        -->
        <div v-if="!validating" class="mb-2" style="width: 100%">
          <v-alert dense outlined type="warning" v-if="selected.length <= 0"
            >At least one layer must be selected
          </v-alert>
          <v-alert dense outlined type="warning" v-if="exceedsCharacterLimit"
            >Selected layers character length of
            {{ Number(selectedCharacterLength).toLocaleString() }} exceeds the
            {{ Number(limits.MAX_AOI_STRING_LENGTH).toLocaleString() }}
            character limit
          </v-alert>
          <v-alert dense outlined type="warning" v-if="exceedsVertexLimit"
            >Selected layers vertices count of
            {{ Number(vertexCount).toLocaleString() }} exceeds the
            {{ Number(limits.MAX_VERTEX_COUNT).toLocaleString() }} vertex limit
          </v-alert>
          <v-alert
            dense
            outlined
            type="warning"
            v-if="selected.length > 0 && !hasSupportedType"
            >Note that <strong>only</strong> Polygon and Multipolygn types are
            supported
          </v-alert>
        </div>
        <div class="d-flex flex-row" style="width: 100%">
          <css-spinner v-if="validating">Validating...</css-spinner>
          <v-spacer></v-spacer>
          <v-btn outlined @click.prevent="cancel" title="Cancel">Cancel</v-btn>
          <v-btn
            id="add-button"
            color="primary"
            class="ml-6 black--text"
            :disabled="!isValid || validating"
            @click.prevent="submit"
          >
            Add
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CssSpinner from "@/components/shared/CssSpinner";
import {
  getSelectedCharacterLength,
  getSelectedVerticesCount,
} from "./UploadApi.js";

export default {
  components: { CssSpinner },
  props: {
    fileName: {
      type: String,
      required: false,
    },
    layers: {
      type: Array,
      required: true,
    },
    limits: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isVisible: false,
      selected: [],
      headers: [
        {
          text: "Layer Name",
          align: "start",
          value: "fileName",
        },
        { text: "# of features", value: "features" },
        { text: "Types", value: "types" },
      ],
      validating: false,
      vertexCount: 0,
      selectedCharacterLength: 0,
    };
  },
  watch: {
    layers(newVal, oldVal) {
      // reset selection when we get new layers
      this.selected = [];
    },
    selected: {
      handler: function (newVal, oldVal) {
        // update everything
        this.validating = true;

        setTimeout(
          async function () {
            this.selectedCharacterLength = await getSelectedCharacterLength(
              newVal
            );
            this.vertexCount = await getSelectedVerticesCount(newVal);
            this.validating = false;
          }.bind(this),
          50
        );
      },
    },
  },
  computed: {
    isValid() {
      return (
        this.selected.length > 0 &&
        !this.exceedsCharacterLimit &&
        !this.exceedsVertexLimit
      );
    },
    indexedLayers() {
      return this.layers.map((item, index) => ({
        id: index,
        types: this.getFeatureCollectionTypes(item),
        ...item,
      }));
    },
    hasSupportedType() {
      const supportedTypes = ["Polygon", "MultiPolygon"];
      for (let i = 0; i < this.selected.length; i++) {
        const layer = this.selected[i];
        for (let k = 0; k < supportedTypes.length; k++) {
          const supportedType = supportedTypes[k];
          if (layer.types.includes(supportedType)) {
            return true;
          }
        }
      }

      return false;
    },
    exceedsCharacterLimit() {
      return this.selectedCharacterLength > this.limits.MAX_AOI_STRING_LENGTH;
    },
    exceedsVertexLimit() {
      return this.vertexCount > this.limits.MAX_VERTEX_COUNT;
    },
  },
  methods: {
    show() {
      this.isVisible = true;
    },
    cancel() {
      this.isVisible = false;
    },
    submit() {
      this.$emit("submit", this.selected, this.fileName);
      this.isVisible = false;
    },
    getFeatureCollectionTypes(featureCollection) {
      const types = [];

      if (
        featureCollection &&
        featureCollection.features &&
        Array.isArray(featureCollection.features)
      ) {
        featureCollection.features.forEach((feature) => {
          const type =
            feature.geometry && feature.geometry.type
              ? feature.geometry.type
              : null;

          if (type && !types.includes(type)) {
            types.push(type);
          }
        });
      }

      if (types.length === 0) {
        return "N/A";
      }

      return types.join(", ");
    },
  },
};
</script>
